// Where the Usage screen's levels live in each shell (ADR-101). The AllCode admin keeps the
// customer's org in the route (`/customers/:orgId/usage[/teams/:teamId[/users/:userId]]`); the
// proxy's Admin area shows its own org, so its routes carry no org (`/admin/usage[/teams/…]`).
// Both end the same way, so a drill-down differs only by its root, taken from the host.
import type { Host } from "../../host/host";

/** Where the drill-down is: the org's teams, one team's users, or one user's models. */
export type UsageDrill =
  | { readonly level: "org" }
  | { readonly level: "team"; readonly teamId: string }
  | { readonly level: "user"; readonly teamId: string; readonly userId: string };

/** The AllCode admin's route patterns, which its route table registers (`web/admin/src/screens/orgs/index.ts`). */
export const USAGE_PATH = "/customers/:orgId/usage";
export const USAGE_TEAM_PATH = "/customers/:orgId/usage/teams/:teamId";
export const USAGE_USER_PATH = "/customers/:orgId/usage/teams/:teamId/users/:userId";

/** The drill-down a route's parameters name; a route without a team is the org's level. */
export function usageDrillFrom(params: object): UsageDrill {
  const { teamId, userId } = params as { teamId?: string; userId?: string };
  if (!teamId) return { level: "org" };
  if (!userId) return { level: "team", teamId };
  return { level: "user", teamId, userId };
}

/** The part of a host a drill-down path needs. */
export type UsageHost = Pick<Host, "kind" | "basePath">;

/** The Usage screen's root in a shell: the customer's page in the AllCode admin, the area's own section on the proxy. */
export function usageRoot(host: UsageHost, orgId: string): string {
  return host.kind === "allcode-admin" ? `${host.basePath}/customers/${encodeURIComponent(orgId)}/usage` : `${host.basePath}/usage`;
}

/** The route of a drill-down level in the host's shell, with the explorer's search string (filters and sort) kept. */
export function usageDrillPath(host: UsageHost, orgId: string, drill: UsageDrill, search = ""): string {
  const enc = encodeURIComponent;
  const base = usageRoot(host, orgId);
  const path =
    drill.level === "org"
      ? base
      : drill.level === "team"
        ? `${base}/teams/${enc(drill.teamId)}`
        : `${base}/teams/${enc(drill.teamId)}/users/${enc(drill.userId)}`;
  return search === "" ? path : `${path}?${search}`;
}
